import { useState } from 'react'
import { ChevronDown, ChevronRight, DollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CostBreakdown as CostBreakdownType, CostItem } from '../../api/telemetry'
import { formatCurrencyUSD } from '../../i18n/formatters'

const CATEGORY_LABELS: Record<string, string> = {
  stt: 'Speech-to-Text',
  llm: 'LLM',
  tts: 'Text-to-Speech',
  vapi: 'VAPI Platform',
  retell: 'Retell Platform',
}

// VAPI calls populate the four classic categories. Retell calls
// currently bill as a single combined per-minute line item, so most
// Retell rows will only show the ``retell`` category — that's expected.
const CATEGORY_ORDER = ['llm', 'tts', 'stt', 'vapi', 'retell']

function formatCost(value: number | undefined): string {
  if (value == null || value === 0) return formatCurrencyUSD(0, 4)
  return formatCurrencyUSD(value, 4)
}

function providerLabel(item: CostItem): string | null {
  if (item.transcriber) return `${item.transcriber.provider} ${item.transcriber.model}`
  if (item.model) return `${item.model.provider} ${item.model.model}`
  if (item.voice) return `${item.voice.provider}${item.voice.model ? ` ${item.voice.model}` : ''}`
  return null
}

function usageSummary(item: CostItem): string | null {
  const parts: string[] = []
  if (item.promptTokens != null && item.promptTokens > 0)
    parts.push(`${item.promptTokens.toLocaleString()} prompt tokens`)
  if (item.completionTokens != null && item.completionTokens > 0)
    parts.push(`${item.completionTokens.toLocaleString()} completion tokens`)
  if (item.characters != null && item.characters > 0)
    parts.push(`${item.characters.toLocaleString()} characters`)
  if (item.minutes != null && item.minutes > 0)
    parts.push(`${item.minutes.toFixed(2)} min`)
  return parts.length > 0 ? parts.join(' · ') : null
}

function BreakdownRow({
  label,
  cost,
  provider,
  usage,
}: {
  label: string
  cost: number | undefined
  provider?: string | null
  usage?: string | null
}) {
  return (
    <div className="flex items-start justify-between gap-2 py-1.5">
      <div className="min-w-0">
        <span className="text-sm text-text-primary">{label}</span>
        {provider && (
          <span className="ml-2 text-xs text-text-muted">{provider}</span>
        )}
        {usage && (
          <div className="text-xs text-text-muted mt-0.5">{usage}</div>
        )}
      </div>
      <span className="shrink-0 text-sm font-mono text-text-primary">{formatCost(cost)}</span>
    </div>
  )
}

export function CostBreakdownPanel({ costUsd, costBreakdown }: {
  costUsd?: number
  costBreakdown?: CostBreakdownType | null
}) {
  const { t } = useTranslation('calls')
  const [expanded, setExpanded] = useState(false)

  // Nothing to show at all
  if (costUsd == null || costUsd <= 0) return null

  const hasBreakdown = costBreakdown?.breakdown && Object.keys(costBreakdown.breakdown).length > 0
  const items = costBreakdown?.items ?? []

  // Build a lookup from items for provider/usage detail per category
  const itemsByType: Record<string, CostItem[]> = {}
  for (const item of items) {
    const key = item.type === 'transcriber' ? 'stt' :
                item.type === 'model' ? 'llm' :
                item.type === 'voice' ? 'tts' :
                item.type
    if (!itemsByType[key]) itemsByType[key] = []
    itemsByType[key].push(item)
  }

  // Analysis items (summary, successEvaluation, etc.)
  const analysisItems = items.filter(i => i.type === 'analysis')

  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-5">
      <button
        className="flex w-full items-center gap-2 text-left"
        onClick={() => hasBreakdown && setExpanded(!expanded)}
        disabled={!hasBreakdown}
      >
        <DollarSign className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-text-primary">{t('detail.cost.title')}</h3>
        <span className="ml-auto font-mono text-base font-semibold text-text-primary">
          {formatCost(costUsd)}
        </span>
        {hasBreakdown && (
          expanded
            ? <ChevronDown className="h-4 w-4 text-text-muted" />
            : <ChevronRight className="h-4 w-4 text-text-muted" />
        )}
      </button>

      {expanded && hasBreakdown && (
        <div className="mt-3 border-t border-border pt-3 space-y-0.5">
          {/* Main cost categories */}
          {CATEGORY_ORDER.map(key => {
            const value = costBreakdown!.breakdown[key as keyof typeof costBreakdown.breakdown] as number | undefined
            if (value == null || value === 0) return null

            const relatedItems = itemsByType[key] ?? []
            // Use the first non-analysis item for provider info
            const mainItem = relatedItems.find(i => i.type !== 'analysis')

            return (
              <BreakdownRow
                key={key}
                label={CATEGORY_LABELS[key] || key}
                cost={value}
                provider={mainItem ? providerLabel(mainItem) : null}
                usage={mainItem ? usageSummary(mainItem) : null}
              />
            )
          })}

          {/* Analysis costs */}
          {analysisItems.length > 0 && analysisItems.some(i => i.cost > 0) && (
            <>
              <div className="border-t border-border/50 my-2" />
              <div className="text-xs font-medium uppercase tracking-wider text-text-muted mb-1">{t('detail.cost.analysis')}</div>
              {analysisItems.map((item, idx) => (
                item.cost > 0 && (
                  <BreakdownRow
                    key={`analysis-${idx}`}
                    label={item.analysisType ?? 'analysis'}
                    cost={item.cost}
                    provider={item.model ? `${item.model.provider} ${item.model.model}` : null}
                    usage={usageSummary(item)}
                  />
                )
              ))}
            </>
          )}

          {/* Total line */}
          <div className="border-t border-border pt-2 mt-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-text-primary">{t('detail.cost.total')}</span>
            <span className="font-mono text-sm font-semibold text-text-primary">
              {formatCost(costBreakdown!.breakdown.total ?? costUsd)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
